<!--
  Inline formularz dodawania tablicy do listy poszukiwanych — zastępuje
  w głównej zawartości listę pojazdów na czas dodawania. Trzy kroki:
    1) Dane tablicy (numer + opis + opcjonalna ważność),
    2) Wybór grup kontaktów (dziedziczony ze sticky paska, edytowalny),
    3) Podsumowanie (czytelny tekst „X osób z Y grup”).
  Nie jest modalem — całość widoku zmienia się płynnie w obrębie strony.
-->
<template>
  <section class="wanted-form">
    <header class="wanted-form__head">
      <h3 class="wanted-form__title">{{ title }}</h3>
      <nav class="wanted-form__steps" :aria-label="t('wizard-steps')">
        <div
          v-for="(label, idx) in stepLabels"
          :key="idx"
          class="step"
          :class="{ 'step--active': step === idx, 'step--done': step > idx }"
        >
          <span class="step__index">{{ idx + 1 }}</span>
          <span class="step__label">{{ label }}</span>
        </div>
      </nav>
    </header>

    <div class="wanted-form__body">
      <!-- Krok 1: dane tablicy -->
      <div v-if="step === 0" class="form-step">
        <div class="form-row">
          <label for="wf-plate">{{ t('plate-no') }} *</label>
          <input
            id="wf-plate"
            v-model="plate"
            type="text"
            class="form-control"
            :placeholder="t('plate-no')"
            autocomplete="off"
            maxlength="10"
            @keyup.enter="goNext"
          />
        </div>
        <div class="form-row">
          <label for="wf-desc">{{ t('vehicle-description') }}</label>
          <input id="wf-desc" v-model="description" type="text" class="form-control" maxlength="100" />
        </div>

        <div v-if="validityPeriodEnabled" class="form-row">
          <label class="checkbox-line">
            <input type="checkbox" v-model="isTimeLimited" />
            {{ t('wanted-time-limit') }}
          </label>
        </div>
        <div v-if="validityPeriodEnabled && isTimeLimited" class="form-row form-row--dates">
          <div>
            <label for="wf-date-from">{{ t('from') }}</label>
            <input id="wf-date-from" v-model="dateFrom" type="date" class="form-control" />
          </div>
          <div>
            <label for="wf-date-to">{{ t('to') }}</label>
            <input id="wf-date-to" v-model="dateTo" type="date" class="form-control" />
          </div>
        </div>

        <p v-if="step1Error" class="step-error">{{ step1Error }}</p>
      </div>

      <!-- Krok 2: wybór grup -->
      <div v-else-if="step === 1" class="form-step">
        <p class="step-hint">{{ t('wizard-step2-hint') }}</p>

        <input
          v-model="groupSearch"
          type="text"
          class="form-control search-input"
          :placeholder="t('search-group-placeholder')"
          autocomplete="off"
        />

        <ul v-if="filteredGroups.length > 0" class="group-list">
          <li
            v-for="group in filteredGroups"
            :key="group.id"
            class="group-item"
            :class="{ 'group-item--selected': draftSet.has(group.id) }"
            @click="toggleGroup(group.id)"
          >
            <label class="group-item__label" @click.stop>
              <input type="checkbox" :checked="draftSet.has(group.id)" @change="toggleGroup(group.id)" />
              <span class="group-item__name">{{ group.name }}</span>
            </label>
            <span class="group-item__counts">
              <span class="count-pill">{{ group.contactsSummary?.count ?? 0 }} {{ t('recipients-short') }}</span>
              <span v-if="(group.contactsSummary?.emails ?? 0) > 0" class="count-pill count-pill--email">
                {{ group.contactsSummary.emails }} e-mail
              </span>
              <span v-if="(group.contactsSummary?.sms ?? 0) > 0" class="count-pill count-pill--sms">
                {{ group.contactsSummary.sms }} SMS
              </span>
              <span v-if="(group.contactsSummary?.mms ?? 0) > 0" class="count-pill count-pill--mms">
                {{ group.contactsSummary.mms }} MMS
              </span>
            </span>
          </li>
        </ul>
        <p v-else class="empty-state">
          {{ groupSearch ? t('no-matches') : t('no-contact-groups') }}
        </p>

        <p v-if="step2Error" class="step-error">{{ step2Error }}</p>
      </div>

      <!-- Krok 3: podsumowanie -->
      <div v-else class="form-step">
        <p class="summary-headline">{{ summaryHeadline }}</p>
        <div class="summary-block">
          <div><strong>{{ t('plate-no') }}:</strong> {{ plate }}</div>
          <div v-if="description"><strong>{{ t('vehicle-description') }}:</strong> {{ description }}</div>
          <div v-if="validityPeriodEnabled && isTimeLimited">
            <strong>{{ t('wanted-validity') }}:</strong>
            {{ dateFrom }} → {{ dateTo }}
          </div>
          <div class="summary-recipients">
            <strong>{{ t('wanted-active-recipients') }}:</strong>
            <span class="chips">
              <span v-for="g in selectedGroupObjects" :key="g.id" class="chip">{{ g.name }}</span>
            </span>
          </div>
          <div class="summary-counts">
            <span class="count-pill">{{ recipientsSummary.count }} {{ t('recipients-short') }}</span>
            <span v-if="recipientsSummary.emails > 0" class="count-pill count-pill--email">
              {{ recipientsSummary.emails }} e-mail
            </span>
            <span v-if="recipientsSummary.sms > 0" class="count-pill count-pill--sms">
              {{ recipientsSummary.sms }} SMS
            </span>
            <span v-if="recipientsSummary.mms > 0" class="count-pill count-pill--mms">
              {{ recipientsSummary.mms }} MMS
            </span>
          </div>
        </div>
      </div>
    </div>

    <footer class="wanted-form__foot">
      <button v-if="step > 0" type="button" class="button-add-user" @click="goBack">
        {{ t('back') }}
      </button>
      <span class="spacer"></span>
      <button type="button" class="button-add-user" @click="cancel">{{ t('cancel') }}</button>
      <button v-if="step < 2" type="button" class="button-save" @click="goNext">
        {{ t('next') }}
      </button>
      <button v-else type="button" class="button-save" :disabled="submitting" @click="confirm">
        {{ submitting ? '...' : t('add') }}
      </button>
    </footer>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useWantedStore } from '@/stores/wanted.store';

const props = defineProps({
  listType: { type: String, default: 'black' }, // 'black' | 'white'
  validityPeriodEnabled: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'cancel']);

const t = getCurrentInstance()?.appContext.config.globalProperties.t ?? (k => k);

const wantedStore = useWantedStore();

const step = ref(0);
const plate = ref('');
const description = ref('');
const isTimeLimited = ref(false);
const dateFrom = ref('');
const dateTo = ref('');
const draft = ref([]);
const groupSearch = ref('');
const step1Error = ref(null);
const step2Error = ref(null);

const draftSet = computed(() => new Set(draft.value));

const stepLabels = computed(() => [
  t('wizard-step1'),
  t('wizard-step2'),
  t('wizard-step3'),
]);

const title = computed(() => {
  if (props.listType === 'white') return t('add-to-white-list');
  return t('add-to-black-list');
});

const filteredGroups = computed(() => {
  const phrase = groupSearch.value.trim().toLowerCase();
  if (!phrase) return wantedStore.contactGroups;
  return wantedStore.contactGroups.filter(g => String(g.name ?? '').toLowerCase().includes(phrase));
});

const selectedGroupObjects = computed(() =>
  wantedStore.contactGroups.filter(g => draftSet.value.has(g.id))
);

const recipientsSummary = computed(() => {
  const totals = { groups: 0, count: 0, emails: 0, sms: 0, mms: 0 };
  for (const g of selectedGroupObjects.value) {
    const s = g.contactsSummary ?? {};
    totals.groups += 1;
    totals.count += Number(s.count ?? 0);
    totals.emails += Number(s.emails ?? 0);
    totals.sms += Number(s.sms ?? 0);
    totals.mms += Number(s.mms ?? 0);
  }
  return totals;
});

const summaryHeadline = computed(() => {
  const listLabel = props.listType === 'white' ? t('white-list').toLowerCase() : t('black-list').toLowerCase();
  return t('wizard-summary-headline', {
    plate: plate.value,
    list: listLabel,
    // count: recipientsSummary.value.count,
    // groups: recipientsSummary.value.groups,
  });
});

onMounted(() => {
  // Wybór grup zaczyna się pusty — operator wskazuje odbiorców jawnie przy
  // każdym dodaniu tablicy (sticky-bar został usunięty).
  draft.value = [];
});

function validateStep1() {
  step1Error.value = null;
  const normalized = String(plate.value ?? '').trim().toUpperCase();
  if (!normalized) {
    step1Error.value = t('plate-no-required');
    return false;
  }
  plate.value = normalized;
  if (props.validityPeriodEnabled && isTimeLimited.value) {
    if (!dateFrom.value || !dateTo.value) {
      step1Error.value = t('wanted-validity-required');
      return false;
    }
    if (dateFrom.value > dateTo.value) {
      step1Error.value = t('wanted-validity-range-invalid');
      return false;
    }
  }
  return true;
}

function validateStep2() {
  step2Error.value = null;
  if (draft.value.length === 0) {
    step2Error.value = t('wanted-group-required');
    return false;
  }
  return true;
}

function goNext() {
  if (step.value === 0 && !validateStep1()) return;
  if (step.value === 1 && !validateStep2()) return;
  step.value = Math.min(step.value + 1, 2);
}

function goBack() {
  step.value = Math.max(step.value - 1, 0);
}

function toggleGroup(id) {
  const num = Number(id);
  if (draft.value.includes(num)) {
    draft.value = draft.value.filter(x => x !== num);
  } else {
    draft.value = [...draft.value, num];
  }
}

function confirm() {
  if (!validateStep1() || !validateStep2()) return;
  emit('submit', {
    plateLpr: plate.value,
    description: description.value,
    validFrom: (props.validityPeriodEnabled && isTimeLimited.value) ? dateFrom.value : null,
    validTo:   (props.validityPeriodEnabled && isTimeLimited.value) ? dateTo.value   : null,
    contactGroupIds: [...draft.value],
  });
}

function cancel() {
  emit('cancel');
}
</script>

<style scoped>
.wanted-form {
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.wanted-form__head {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-subtle);
  background-color: var(--surface-secondary);
}
.wanted-form__title {
  margin: 0 0 0.75rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: 0.01em;
}

/* ---- pasek kroków --------------------------------------------------- */
.wanted-form__steps {
  display: flex;
  gap: 0;
  flex-wrap: wrap;
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  padding: 0.3rem;
}
.step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.85rem;
  border-radius: var(--control-radius);
  background-color: transparent;
  color: var(--text-secondary);
  border: 0;
  font-size: 0.85rem;
  font-weight: var(--font-weight-medium);
  flex: 1;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.step--active {
  background-color: var(--status-ok);
  color: var(--text-inverse);
}
.step--done {
  color: var(--status-ok);
  background-color: transparent;
}
.step__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background-color: var(--border-subtle);
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}
.step--active .step__index { background-color: var(--surface-primary); color: var(--text-primary); }
.step--done .step__index   { background-color: var(--status-ok); color: var(--text-inverse); }

/* ---- body / formularze --------------------------------------------- */
.wanted-form__body {
  padding: 1.25rem;
}
.form-step { display: flex; flex-direction: column; gap: 1rem; }
.form-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.form-row label {
  font-size: 0.85rem;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.form-row :deep(.form-control) {
  border-radius: var(--control-radius);
}
.form-row--dates {
  flex-direction: row;
  gap: 1rem;
}
.form-row--dates > div {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.checkbox-line {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  font-weight: var(--font-weight-medium);
  font-size: 0.92rem;
  color: var(--text-primary);
  padding: 0.55rem 0.75rem;
  background-color: var(--surface-secondary);
  border: 1px solid var(--border-subtle);
  border-radius: var(--control-radius);
  text-transform: none;
  letter-spacing: 0;
}
.checkbox-line input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  accent-color: var(--accent-blue);
  cursor: pointer;
}
.step-hint {
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.92rem;
}
.step-error {
  color: var(--status-error-strong);
  margin: 0;
  padding: 0.55rem 0.85rem;
  background-color: var(--status-error-soft);
  border: 1px solid var(--status-error-border);
  border-radius: var(--control-radius);
  font-size: 0.9rem;
  font-weight: var(--font-weight-medium);
}

.search-input {
  margin-bottom: 0.5rem;
  border-radius: var(--control-radius);
}

.group-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  background-color: var(--surface-primary);
  max-height: 22rem;
  overflow-y: auto;
}
.group-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.9rem;
  border-bottom: 1px solid var(--border-subtle);
  cursor: pointer;
  color: var(--text-primary);
  transition: background-color 0.12s ease;
  position: relative;
}
.group-item:last-child { border-bottom: 0; }
.group-item:hover { background-color: var(--surface-secondary); }
.group-item--selected {
  background-color: var(--accent-blue-soft);
  box-shadow: inset 3px 0 0 var(--accent-blue);
}
.group-item--selected:hover { background-color: var(--accent-blue-soft); }
.group-item__label {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
  cursor: pointer;
  min-width: 0;
}
.group-item__label input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  accent-color: var(--accent-blue);
  cursor: pointer;
}
.group-item__name {
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
}
.group-item__counts {
  display: inline-flex;
  gap: 0.3rem;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.count-pill {
  background-color: var(--surface-secondary);
  border: 1px solid var(--border-default);
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}
.count-pill--email { color: var(--accent-blue); background-color: var(--accent-blue-soft); border-color: var(--accent-blue-border); }
.count-pill--sms   { color: var(--status-ok); background-color: var(--status-ok-soft); border-color: var(--status-ok-border); }
.count-pill--mms   { color: var(--status-warn-strong); background-color: var(--status-warn-soft); border-color: var(--status-warn-border); }

.empty-state {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--text-secondary);
  background-color: var(--surface-secondary);
  border: 1px dashed var(--border-default);
  border-radius: var(--control-radius);
}

/* ---- podsumowanie (krok 3) ----------------------------------------- */
.summary-headline {
  margin: 0;
  font-size: 1rem;
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  padding: 0.75rem 1rem;
  background-color: var(--status-ok-soft);
  border: 1px solid var(--status-ok-border);
  border-left: 4px solid var(--status-ok);
  border-radius: var(--control-radius);
}
.summary-block {
  background-color: var(--surface-secondary);
  border: 1px solid var(--border-subtle);
  border-radius: var(--control-radius);
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  color: var(--text-primary);
}
.summary-block strong {
  color: var(--text-secondary);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: var(--font-weight-medium);
  margin-right: 0.4rem;
}
.summary-recipients .chips {
  display: inline-flex;
  gap: 0.3rem;
  margin-left: 0;
  flex-wrap: wrap;
}
.chip {
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: 999px;
  padding: 0.15rem 0.7rem;
  font-size: 0.82rem;
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
}
.summary-counts {
  display: inline-flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

/* ---- stopka --------------------------------------------------------- */
.wanted-form__foot {
  display: flex;
  align-items: center;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid var(--border-subtle);
  gap: 0.55rem;
  background-color: var(--surface-secondary);
}
.spacer { flex: 1; }
.button-save {
  border-radius: 6px !important;
  padding: 0.45rem 0.95rem;
}

@media (max-width: 640px) {
  .form-row--dates { flex-direction: column; gap: 0.75rem; }
  .step { padding: 0.4rem 0.5rem; font-size: 0.78rem; }
  .step__label { display: none; }
}
</style>
